import { useState, useEffect, useRef, useCallback } from 'react';
import { Box, IconButton, MenuItem, Tooltip, Stack, CircularProgress } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { OPERATOR_LABELS } from '../filterConfig';
import { CustomTextField, CustomDatePicker, CustomAutocomplete } from '../../../../utils/StyledInputs';
import API from '../../../../api';

dayjs.locale('it');

const NO_VALUE_OPERATORS = ['is_null', 'is_not_null'];

/**
 * Single condition row in the filter builder.
 * All inputs on a single row: [Campo] [Operatore] [Valore] [Valore2?] [Elimina]
 */
export default function ConditionRow({ condition, index, fields, onChange, onRemove, isOnly }) {
  const selectedField = fields.find((f) => f.field === condition.field);
  const isRelationField = !!selectedField?.field?.includes('.');
  const availableOperators = (selectedField?.allowed_operators || []).filter(
    (op) => op !== 'not_exists' || isRelationField
  );
  // console.log('Selected field:', selectedField);
  const fieldType = selectedField?.field_type || 'string';
  const isBetween = condition.operator === 'between';
  const needsValue = !NO_VALUE_OPERATORS.includes(condition.operator);
  const showValue = condition.operator && needsValue;

  // --- API autocomplete state ---
  const [apiOptions, setApiOptions] = useState([]);
  const [apiLoading, setApiLoading] = useState(false);
  const [apiInputValue, setApiInputValue] = useState('');
  const debounceRef = useRef(null);

  // Check if this field uses an API source
  const optionsSource = selectedField?.options_source || '';
  const isApiSource = optionsSource.startsWith('api:');
  const apiEndpoint = isApiSource ? optionsSource.split(':')[1].replace(/^\/|\/$/g, '') : '';

  // --- Composite field support ---
  const isComposite = fieldType === 'composite';
  const compositeFieldNames = isComposite ? selectedField.field.split('.').pop().split(',') : [];

  // Parse options_source: "z_authorization_objects:id,name;z_authorization_types:id,description"
  // into [{ tableName, valueField, labelField }, ...]
  const compositeParts = isComposite
    ? optionsSource.split(';').map((part) => {
        const [tableName, fieldsDef] = part.split(':');
        const [valueField, labelField] = fieldsDef.split(',');
        return { tableName, valueField, labelField };
      })
    : [];

  // Build options for each sub-field from selectedField.options[tableName]
  // The backend already returns objects with { value, label }
  const compositeOptions = isComposite
    ? compositeFieldNames.map((_, idx) => {
        const { tableName } = compositeParts[idx] || {};
        return selectedField?.options?.[tableName] || [];
      })
    : [];

  // Reset API options when field changes
  useEffect(() => {
    setApiOptions([]);
    setApiInputValue('');
  }, [condition.field]);

  // Debounced API search
  const fetchApiOptions = useCallback(
    (query) => {
      if (!apiEndpoint || query.length < 3) {
        setApiOptions([]);
        return;
      }

      setApiLoading(true);
      API.get(apiEndpoint, { params: { filter: query } })
        .then((response) => {
          const data = response.data?.data || response.data || [];
          setApiOptions(Array.isArray(data) ? data : []);
        })
        .catch(() => {
          setApiOptions([]);
        })
        .finally(() => setApiLoading(false));
    },
    [apiEndpoint]
  );

  const handleApiInputChange = (event, value) => {
    setApiInputValue(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (value.length >= 3) {
      debounceRef.current = setTimeout(() => fetchApiOptions(value), 500);
    } else {
      setApiOptions([]);
    }
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleChange = (key, value) => {
    const updated = { ...condition, [key]: value };

    if (key === 'field') {
      updated.operator = '';
      updated.value = '';
      updated.value2 = '';
    }
    if (key === 'operator') {
      updated.value = '';
      updated.value2 = '';
    }

    onChange(index, updated);
  };

  const renderApiAutocomplete = () => {
    const isMultiple = condition.operator === 'in' || condition.operator === 'not_in';

    if (isMultiple) {
      const rawValue = condition.value || [];
      const selectedValues = Array.isArray(rawValue) ? rawValue : rawValue.split(',').filter(Boolean);
      const valueObjects = selectedValues.map((v) => apiOptions.find((opt) => opt.value === v)).filter(Boolean);
      return (
        <Box sx={{ flex: 1, minWidth: 120 }}>
          <CustomAutocomplete
            options={apiOptions}
            value={valueObjects}
            getOptionLabel={(option) => option.label || ''}
            onChange={(e, val) => handleChange('value', val.map((v) => v.value).join(','))}
            onInputChange={handleApiInputChange}
            inputValue={apiInputValue}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            loading={apiLoading}
            noOptionsText={apiInputValue.length < 3 ? 'Digita almeno 3 caratteri...' : 'Nessun risultato'}
            renderInput={(params) => (
              <CustomTextField
                {...params}
                label="Valori"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {apiLoading ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            multiple
            filterOptions={(x) => x}
          />
        </Box>
      );
    }

    const singleValue = apiOptions.find((opt) => opt.value === condition.value) || null;
    return (
      <Box sx={{ flex: 1, minWidth: 120 }}>
        <CustomAutocomplete
          options={apiOptions}
          value={singleValue}
          getOptionLabel={(option) => option.label || ''}
          onChange={(e, val) => handleChange('value', val ? val.value : '')}
          onInputChange={handleApiInputChange}
          inputValue={apiInputValue}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          loading={apiLoading}
          noOptionsText={apiInputValue.length < 3 ? 'Digita almeno 3 caratteri...' : 'Nessun risultato'}
          renderInput={(params) => (
            <CustomTextField
              {...params}
              label="Valore"
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {apiLoading ? <CircularProgress size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          filterOptions={(x) => x}
        />
      </Box>
    );
  };

  const getCompositeValue = () => {
    try {
      return typeof condition.value === 'string' && condition.value ? JSON.parse(condition.value) : {};
    } catch {
      return {};
    }
  };

  const handleCompositeFieldChange = (subField, val) => {
    const current = getCompositeValue();
    const updated = { ...current };
    if (val) {
      updated[subField] = val;
    } else {
      delete updated[subField];
    }
    handleChange('value', Object.keys(updated).length > 0 ? JSON.stringify(updated) : '');
  };

  const compositeLabels = ['Oggetto', 'Tipo'];

  const renderCompositeInputs = () => {
    const compositeValue = getCompositeValue();

    return compositeFieldNames.map((subField, idx) => {
      const options = compositeOptions[idx] || [];
      const selectedOption = options.find((opt) => opt.value === compositeValue[subField]) || null;

      return (
        <Box key={subField} sx={{ flex: 1, minWidth: 120 }}>
          <CustomAutocomplete
            options={options}
            value={selectedOption}
            getOptionLabel={(option) => option?.label || ''}
            onChange={(e, val) => handleCompositeFieldChange(subField, val ? val.value : '')}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            noOptionsText="Nessun risultato"
            renderInput={(params) => <CustomTextField {...params} label={compositeLabels[idx]} size="small" />}
          />
        </Box>
      );
    });
  };

  const renderValueInputs = () => {
    if (!showValue) return null;

    // Composite fields
    if (isComposite) return renderCompositeInputs();

    // API-sourced fields take priority
    if (isApiSource) return renderApiAutocomplete();

    switch (fieldType) {
      case 'enum':
        return (
          <Box sx={{ flex: 1, minWidth: 120 }}>
            <CustomTextField
              select
              label="Valore"
              size="small"
              value={condition.value}
              onChange={(e) => handleChange('value', e.target.value)}
            >
              {selectedField?.options?.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </CustomTextField>
          </Box>
        );

      case 'date':
        return (
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
            <Box sx={{ flex: 1, minWidth: 140 }}>
              <CustomDatePicker
                label={isBetween ? 'Da' : 'Valore'}
                value={condition.value ? dayjs(condition.value) : null}
                onChange={(val) => handleChange('value', val ? val.format('YYYY-MM-DD') : '')}
              />
            </Box>
            {isBetween && (
              <Box sx={{ flex: 1, minWidth: 140 }}>
                <CustomDatePicker
                  label="A"
                  value={condition.value2 ? dayjs(condition.value2) : null}
                  onChange={(val) => handleChange('value2', val ? val.format('YYYY-MM-DD') : '')}
                />
              </Box>
            )}
          </LocalizationProvider>
        );

      case 'array': {
        const options = selectedField?.options || [];
        const isMultiple = condition.operator === 'in' || condition.operator === 'not_in';

        if (isMultiple) {
          const rawValue = condition.value || [];
          const selectedValues = Array.isArray(rawValue) ? rawValue : rawValue.split(',').filter(Boolean);
          const valueObjects = selectedValues.map((v) => options.find((opt) => opt.value === v)).filter(Boolean);
          const allSelected = options.length > 0 && valueObjects.length === options.length;
          const SELECT_ALL = '__select_all__';
          const selectAllOption = {
            value: SELECT_ALL,
            label: allSelected ? 'Deseleziona tutto' : 'Seleziona tutto',
          };
          const displayOptions = options.length > 1 ? [selectAllOption, ...options] : options;
          return (
            <Box sx={{ flex: 1, minWidth: 120 }}>
              <CustomAutocomplete
                options={displayOptions}
                value={valueObjects}
                getOptionLabel={(option) => option.label || ''}
                onChange={(e, val) => {
                  if (val.some((v) => v.value === SELECT_ALL)) {
                    handleChange('value', allSelected ? '' : options.map((o) => o.value).join(','));
                    return;
                  }
                  handleChange('value', val.map((v) => v.value).join(','));
                }}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderOption={(props, option) => {
                  if (option.value === SELECT_ALL) {
                    return (
                      <li
                        {...props}
                        key={SELECT_ALL}
                        style={{
                          fontWeight: 600,
                          color: '#1565c0',
                          borderBottom: '1px solid #eee',
                        }}
                      >
                        {option.label}
                      </li>
                    );
                  }
                  return (
                    <li {...props} key={option.value}>
                      {option.label}
                    </li>
                  );
                }}
                filterOptions={(opts, state) =>
                  opts.filter(
                    (opt) =>
                      opt.value === SELECT_ALL ||
                      (opt.label || '').toLowerCase().includes(state.inputValue.toLowerCase())
                  )
                }
                renderInput={(params) => <CustomTextField {...params} label="Valori" size="small" />}
                multiple
              />
            </Box>
          );
        }

        const singleValue = options.find((opt) => opt.value === condition.value) || null;
        return (
          <Box sx={{ flex: 1, minWidth: 120 }}>
            <CustomAutocomplete
              options={options}
              value={singleValue}
              getOptionLabel={(option) => option.label || ''}
              onChange={(e, val) => handleChange('value', val ? val.value : '')}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => <CustomTextField {...params} label="Valore" size="small" />}
            />
          </Box>
        );
      }

      case 'boolean':
        return (
          <Box sx={{ flex: 1, minWidth: 120 }}>
            <CustomTextField
              select
              label="Valore"
              size="small"
              value={condition.value}
              onChange={(e) => handleChange('value', e.target.value)}
            >
              <MenuItem value="true">Si</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </CustomTextField>
          </Box>
        );

      default:
        return (
          <>
            <Box sx={{ flex: 1, minWidth: 140 }}>
              <CustomTextField
                label={isBetween ? 'Da' : 'Valore'}
                size="small"
                value={condition.value}
                onChange={(e) => handleChange('value', e.target.value)}
              />
            </Box>
            {isBetween && (
              <Box sx={{ flex: 1, minWidth: 140 }}>
                <CustomTextField
                  label="A"
                  size="small"
                  value={condition.value2}
                  onChange={(e) => handleChange('value2', e.target.value)}
                />
              </Box>
            )}
          </>
        );
    }
  };

  return (
    <Box sx={{ py: 1.5, px: 1, borderRadius: 1, width: '100%' }}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Box sx={{ flex: 1, minWidth: 160 }}>
          <CustomTextField
            select
            label="Campo"
            size="small"
            value={condition.field}
            onChange={(e) => handleChange('field', e.target.value)}
          >
            {fields.map((f) => (
              <MenuItem key={f.field} value={f.field}>
                {f.field_label}
              </MenuItem>
            ))}
          </CustomTextField>
        </Box>

        <Box sx={{ flex: 1, minWidth: 160 }}>
          <CustomTextField
            select
            label="Operatore"
            size="small"
            value={condition.operator}
            onChange={(e) => handleChange('operator', e.target.value)}
            disabled={!condition.field}
          >
            {availableOperators.map((op) => (
              <MenuItem key={op} value={op}>
                {OPERATOR_LABELS[op]}
              </MenuItem>
            ))}
          </CustomTextField>
        </Box>

        {renderValueInputs()}

        <Tooltip title="Rimuovi condizione">
          <span>
            <IconButton size="small" onClick={() => onRemove(index)} disabled={isOnly}>
              <DeleteIcon fontSize="small" color={isOnly ? 'disabled' : 'inherit'} />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Box>
  );
}
